import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ListTodo, MessageSquare, Settings, Workflow } from "lucide-react";
import { NotificationsBell } from "./notifications-bell";
import { useAgentAccess } from "@/hooks/use-agent-access";
import { useTenant } from "@/context/tenant-context";
import { cn } from "@/lib/utils";

const WORKSPACE_MODES = [
  { key: "chat", label: "Chat", icon: MessageSquare },
  { key: "tasks", label: "Tasks", icon: ListTodo },
  { key: "workflows", label: "Workflows", icon: Workflow },
] as const;

/**
 * Slim global chrome bar for tenant pages. It reserves a dedicated lane for
 * the notifications bell at the top-right so the bell no longer floats over
 * page content (where it used to cover page-level menus and action buttons).
 *
 * Desktop only — the mobile shell provides its own top bar.
 */
export function TenantTopBar({
  onOpenAgentSettings,
}: {
  onOpenAgentSettings?: (agentId: string) => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { tenantId } = useTenant();
  const selectedAgentId = searchParams.get("agentId");
  const { canManage } = useAgentAccess(selectedAgentId);
  const base = `/tenants/${tenantId}`;
  const isDashboard = location.pathname === `${base}/dashboard`;
  const isWorkspace = location.pathname === `${base}/workspace`;
  const isWorkflowRoute = location.pathname.startsWith(`${base}/workflows/`);
  const showWorkspaceModes = isDashboard || isWorkspace || isWorkflowRoute;
  const requestedMode = searchParams.get("mode");
  // Nothing is highlighted inside a workflow. The three buttons stay as a way
  // out, but you are not in any of them — you are one level down, in the
  // canvas or one of its views, and marking "Workflows" as where you are said
  // you were somewhere you had already left.
  const activeMode = isWorkflowRoute
    ? null
    : isDashboard
      ? "chat"
      : WORKSPACE_MODES.some(({ key }) => key === requestedMode)
        ? requestedMode
        : "chat";

  const openMode = (mode: (typeof WORKSPACE_MODES)[number]["key"]) => {
    if (isDashboard && mode === "chat") return;
    const params = new URLSearchParams(searchParams);
    if (mode === "chat") params.delete("mode");
    else params.set("mode", mode);
    const query = params.toString();
    navigate(`${base}/workspace${query ? `?${query}` : ""}`);
  };

  const openAgentSettings = (agentId: string) => {
    onOpenAgentSettings?.(agentId);
  };

  return (
    <div className="grid h-11 shrink-0 grid-cols-[1fr_auto_1fr] items-center border-b border-border bg-surface px-4">
      <div aria-hidden="true" />
      {showWorkspaceModes ? (
        <nav className="flex items-center justify-center gap-1" aria-label="Workspace views">
          {WORKSPACE_MODES.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => openMode(key)}
              data-tour={key === "workflows" ? "mode-workflows" : undefined}
              className={cn(
                "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                activeMode === key
                  ? "bg-accent/10 text-accent"
                  : "text-text-secondary hover:bg-surface-hover hover:text-text-primary",
              )}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </nav>
      ) : (
        <div />
      )}
      <div className="flex items-center justify-self-end gap-2">
        <NotificationsBell />
        {selectedAgentId && canManage && (
          <button
            type="button"
            onClick={() => openAgentSettings(selectedAgentId)}
            data-tour="agent-settings"
            title="Agent settings"
            aria-label="Agent settings"
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-surface-hover hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
          >
            <Settings size={15} />
          </button>
        )}
      </div>
    </div>
  );
}
